import React, { useMemo } from 'react';
import CalcShell from '../../components/CalcShell';
import NumberField from '../../components/NumberField';
import YesNoToggle from '../../components/YesNoToggle';
import RiskScoreResult from '../../components/RiskScoreResult';
import { mipss70RiskGroups, calculateMipss70 } from '../../data/diseasesData';
import { useUserInput } from '../../context/UserInputContext';

const MIPSS70_URL = 'http://www.mipss70score.it';

const Mipss70Calculator: React.FC = () => {
  const { state, update } = useUserInput();
  const input = state.mipss70;
  const setInput = (val: typeof input) => update('mipss70', val);

  const result = useMemo(() => {
    if (!input.sex) return null;
    const hb = parseFloat(input.hemoglobin);
    const blasts = parseFloat(input.blasts);
    if (isNaN(hb) || isNaN(blasts)) return null;
    if (input.constitutionalSymptoms === null || input.calrType1 === null) return null;
    if (!input.hmrCount || !input.karyotype) return null;

    const hmr = input.hmrCount === '2+' ? 2 : parseInt(input.hmrCount, 10);
    if (isNaN(hmr)) return null;

    return calculateMipss70(
      input.sex,
      hb,
      blasts,
      input.constitutionalSymptoms,
      input.calrType1,
      hmr,
      input.karyotype as 'favorable' | 'unfavorable' | 'vhr'
    );
  }, [input]);

  const inputs = (
    <>
      <div className="hema-input-group">
        <label className="hema-label">Sex</label>
        <div className="hema-field">
          <select
            value={input.sex || ''}
            onChange={(e) => setInput({ ...input, sex: (e.target.value || null) as typeof input.sex })}
          >
            <option value="">Select...</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
      </div>

      <NumberField
        label="Hemoglobin (g/dL)"
        value={input.hemoglobin}
        onChange={(v) => setInput({ ...input, hemoglobin: v })}
        placeholder="e.g. 9.5"
        min={0}
        step={0.1}
        decimal
      />

      <NumberField
        label="Circulating blasts (%)"
        value={input.blasts}
        onChange={(v) => setInput({ ...input, blasts: v })}
        placeholder="e.g. 3"
        min={0}
        max={100}
        step={0.1}
        decimal
      />

      <YesNoToggle
        label="Constitutional symptoms"
        value={input.constitutionalSymptoms}
        onChange={(v) => setInput({ ...input, constitutionalSymptoms: v })}
      />

      <YesNoToggle
        label="CALR type 1 / type 1-like mutation"
        value={input.calrType1}
        onChange={(v) => setInput({ ...input, calrType1: v })}
      />

      <div className="hema-input-group">
        <label className="hema-label">HMR mutation count</label>
        <div className="hema-field">
          <select
            value={input.hmrCount}
            onChange={(e) => setInput({ ...input, hmrCount: e.target.value })}
          >
            <option value="">Select...</option>
            <option value="0">0 (no HMR)</option>
            <option value="1">1 HMR mutation</option>
            <option value="2+">≥2 HMR mutations</option>
          </select>
        </div>
        <p className="hema-hint">
          Presence of a mutation in any of the following genes: ASXL1, EZH2, SRSF2, U2AF1 Q157 or IDH1/2
        </p>
      </div>

      <div className="hema-input-group">
        <label className="hema-label">Karyotype</label>
        <div className="hema-field">
          <select
            value={input.karyotype}
            onChange={(e) => setInput({ ...input, karyotype: e.target.value })}
          >
            <option value="">Select...</option>
            <option value="favorable">Favorable / Normal</option>
            <option value="unfavorable">Unfavorable</option>
            <option value="vhr">Very High Risk (VHR)</option>
          </select>
        </div>
        <p className="hema-hint">
          <span>Unfavorable: any abnormal karyotype other than normal karyotype or sole abnormalities of 20q-, 13q-, +9, chromosome 1 translocation/duplication, or -Y or sex chromosome abnormality other than -Y</span>
          <br />
          <span>VHR: single/multiple abnormalities of -7, i(17q), inv(3)/3q21, 12p-/12p11.2, 11q-/11q23, or other autosomal trisomies not including +8/+9 (e.g. +21, +19)</span>
        </p>
      </div>
    </>
  );

  const info = (
    <>
      <p className="calc-shell__info-note">
        MIPSS70+ version 2.0 incorporates sex-specific anemia thresholds, mutually exclusive HMR mutation and karyotype tiers.{' '}
        <a href={MIPSS70_URL} target="_blank" rel="noopener noreferrer">
          Official MIPSS-70 calculator
        </a>
      </p>
    </>
  );

  const aside = result ? (
    <div className="score-display">
      <span className="score-label">MIPSS-70+ v2.0</span>
      <span className="score-value">{result.score}</span>
    </div>
  ) : null;

  return (
    <CalcShell
      title="MIPSS-70+ version 2.0"
      subtitle="Mutation and Karyotype-Enhanced International Prognostic Scoring System"
      info={info}
      inputs={inputs}
      aside={aside}
    >
      <RiskScoreResult
        groups={mipss70RiskGroups}
        activeLevel={result?.level || null}
        emptyHint="Fill in all fields to calculate MIPSS-70+ v2.0"
      />
    </CalcShell>
  );
};

export default Mipss70Calculator;
